// Select tool (spec §5.3): click, shift-click, marquee, move (with snapping and
// Alt-drag duplicate), resize, rotate, nudge, delete.
import type { BoardObject, Card, Connector, Endpoint, Op } from '../../shared/types.ts';
import { byPosition, isColumn } from '../../shared/columns.ts';
import { columnAt, dropIndex, layoutOps, members, roomFor } from '../model/columns.ts';
import type { Editor } from '../editor.ts';
import {
  center, fromLocal, normalizeAngle, rectFromPoints, rotatePoint, toLocal, union,
  type Box, type Rect,
} from '../util/geometry.ts';
import { handleAt, handleCursor, resizeLocal, selectionFrame, type Handle, type ResizeHandle } from '../engine/selection.ts';
import { guideFor, snapMove, snapValue, type SnapMatch } from '../engine/snap.ts';
import { isTextBearing } from '../text/placement.ts';
import { endpointPosition } from '../engine/connectors.ts';
import { withFrameChildren } from '../model/frames.ts';
import { endpointAt } from './connector.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const DRAG_THRESHOLD = 3;           // screen px before a press becomes a drag
const SNAP_PX = 6;                  // screen px
const ROTATE_STEP = Math.PI / 12;   // 15°, with shift
const ENDPOINT_HIT = 8;             // screen px

type Originals = Map<string, BoardObject>;

type State =
  | { kind: 'idle' }
  // hitId is null when pressing empty space inside a multi-selection's frame.
  // tick: pressed on a card's tick box (a click toggles it; a drag moves the card).
  | { kind: 'pending'; sx: number; sy: number; x: number; y: number; hitId: string | null; wasSelected: boolean; alt: boolean; tick?: { cardId: string; itemId: string } }
  | { kind: 'moving'; x: number; y: number; originals: Originals; bounds: Rect; cards?: ColumnDrag }
  | { kind: 'resizing'; handle: ResizeHandle; frame: Box; originals: Originals; keepAspect: boolean }
  | { kind: 'rotating'; cx: number; cy: number; startAngle: number; baseRotation: number; originals: Originals }
  | { kind: 'marquee'; x: number; y: number; base: Set<string> }
  | { kind: 'endpoint'; id: string; end: 'from' | 'to' };

/** Cards being dragged, and the column they'd drop into (spec §13.6). */
interface ColumnDrag {
  ids: string[];          // top to bottom as they were picked up
  lead: string;           // the card under the pointer: its centre picks the column
  sources: Set<string>;   // columns they came from
  target: string | null;
  index: number;
}

export class SelectTool implements Tool {
  cursor = 'default';
  private readonly ed: Editor;
  private state: State = { kind: 'idle' };

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    const ed = this.ed;
    if (e.modKey) {
      const href = ed.linkAt(e.x, e.y);
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    // A selected connector has endpoint handles instead of a resize frame.
    const end = this.endpointHandleAt(e.x, e.y);
    if (end) {
      ed.beginGesture();
      ed.renderer.setActive([end.id]);
      ed.setTransforming(true);
      this.state = { kind: 'endpoint', ...end };
      return;
    }
    const frame = ed.readOnly ? null : selectionFrame(ed.store, ed.selection, (o) => ed.boundsOf(o));
    const handle = frame && this.handleAt(frame, e.x, e.y);
    if (frame && handle) {
      ed.beginGesture();
      const originals = this.beginTransform();
      if (handle === 'rotate') {
        const [cx, cy] = center(frame);
        this.state = {
          kind: 'rotating', cx, cy, originals,
          startAngle: Math.atan2(e.y - cy, e.x - cx), baseRotation: frame.rotation,
        };
      } else {
        const keepAspect = [...originals.values()].some((o) => o.type === 'sticky' || o.type === 'image' || o.type === 'audio');
        this.state = { kind: 'resizing', handle, frame, originals, keepAspect };
      }
      return;
    }

    const footer = e.shiftKey ? null : ed.columnFooterAt(e.x, e.y);
    if (footer) {
      ed.addCardToColumn(footer.id);
      return;
    }

    const tick = ed.readOnly || e.shiftKey ? null : ed.tickBoxAt(e.x, e.y);
    if (tick) {
      const wasSelected = ed.selection.has(tick.cardId);
      this.state = { kind: 'pending', sx: e.sx, sy: e.sy, x: e.x, y: e.y, hitId: tick.cardId, wasSelected, alt: e.altKey, tick };
      return;
    }

    const hit = ed.hitTest(e.x, e.y);
    if (hit) {
      const wasSelected = ed.selection.has(hit.id);
      if (e.shiftKey && wasSelected) {
        ed.setSelection([...ed.selection].filter((id) => id !== hit.id));
        return;
      }
      if (e.shiftKey) ed.setSelection([...ed.selection, hit.id]);
      else if (!wasSelected) ed.setSelection([hit.id]);
      this.state = { kind: 'pending', sx: e.sx, sy: e.sy, x: e.x, y: e.y, hitId: hit.id, wasSelected, alt: e.altKey };
      return;
    }

    if (frame && ed.selection.size > 1 && !e.shiftKey && insideFrame(frame, e.x, e.y)) {
      this.state = { kind: 'pending', sx: e.sx, sy: e.sy, x: e.x, y: e.y, hitId: null, wasSelected: true, alt: e.altKey };
      return;
    }

    this.state = { kind: 'marquee', x: e.x, y: e.y, base: e.shiftKey ? new Set(ed.selection) : new Set() };
    if (!e.shiftKey) ed.setSelection([]);
  }

  onPointerMove(e: WorldPointerEvent): void {
    const s = this.state;
    switch (s.kind) {
      case 'idle':
        return this.hover(e);
      case 'pending':
        if (this.ed.readOnly || Math.hypot(e.sx - s.sx, e.sy - s.sy) < DRAG_THRESHOLD) return;
        if (s.tick && !s.wasSelected) this.ed.setSelection([s.tick.cardId]);
        this.startMove(s.x, s.y, s.alt || e.altKey);
        return this.updateMove(e);
      case 'moving':
        return this.updateMove(e);
      case 'resizing':
        return this.updateResize(s, e);
      case 'rotating':
        return this.updateRotate(s, e);
      case 'marquee':
        return this.updateMarquee(s, e);
      case 'endpoint':
        return this.updateEndpoint(s, e);
    }
  }

  onPointerUp(e: WorldPointerEvent): void {
    const ed = this.ed;
    const s = this.state;
    this.state = { kind: 'idle' };
    switch (s.kind) {
      case 'pending':
        if (s.tick) {
          ed.toggleItem(s.tick.cardId, s.tick.itemId);
          break;
        }
        // A click (no drag) on one of several selected objects selects just it;
        // on empty space inside the selection frame, it deselects.
        if (s.wasSelected && !e.shiftKey && ed.selection.size > 1) ed.setSelection(s.hitId ? [s.hitId] : []);
        break;
      case 'moving':
      case 'resizing':
      case 'rotating':
        if (s.kind === 'resizing') ed.fitTextHeights(s.originals.keys());
        if (s.kind === 'moving' && s.cards) this.dropCards(s.cards);
        this.endTransform();
        ed.endGesture();
        break;
      case 'endpoint':
        ed.attachTarget = null;
        this.endTransform();
        ed.endGesture();
        break;
      case 'marquee':
        ed.marquee = null;
        ed.renderer.invalidateInteractive();
        break;
    }
  }

  onCancel(): void {
    const ed = this.ed;
    const s = this.state;
    this.state = { kind: 'idle' };
    switch (s.kind) {
      case 'moving':
      case 'resizing':
      case 'rotating':
      case 'endpoint':
        ed.attachTarget = null;
        this.endTransform();
        ed.cancelGesture();
        break;
      case 'marquee':
        ed.marquee = null;
        ed.setSelection(s.base);
        ed.renderer.invalidateInteractive();
        break;
    }
  }

  onKeyDown(e: KeyboardEvent): void {
    const ed = this.ed;
    const idle = this.state.kind === 'idle';
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        if (idle) ed.setSelection([]);
        else this.onCancel();
        return;
      case 'Enter': {
        if (!idle || ed.selection.size !== 1) return;
        const o = ed.store.get(ed.selection.values().next().value!);
        if (o?.type === 'card') {
          e.preventDefault();
          ed.openCard(o.id);
          return;
        }
        if (o?.type === 'audio') {
          e.preventDefault();
          ed.audio.toggle(o);
          return;
        }
        if (!o || !isTextBearing(o)) return;
        e.preventDefault();
        ed.startTextEdit(o.id, { caret: 'all' });
        return;
      }
      case 'Delete':
      case 'Backspace':
        if (!idle) return;
        e.preventDefault();
        ed.deleteSelection();
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        if (!idle || !ed.selection.size) return;
        e.preventDefault();
        // Alt+arrows move cards within and between columns (spec §13.6).
        const dir = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as const)[e.key];
        if (e.altKey && ed.moveCardsInColumn(dir)) return;
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        ed.nudge(dx, dy);
        return;
      }
    }
  }

  // --- transforms ---

  /** Snapshots the selection (plus frame contents when moving) and moves it to the interactive layer. */
  private beginTransform(moving = false): Originals {
    const ed = this.ed;
    const objs = moving ? withFrameChildren(ed.store, ed.selectedObjects()) : ed.selectedObjects();
    const originals: Originals = new Map(objs.map((o) => [o.id, o]));
    ed.renderer.setActive(ed.withDependents(originals.keys()));
    ed.setTransforming(true);
    ed.hovered = null;
    return originals;
  }

  private endTransform(): void {
    const ed = this.ed;
    ed.renderer.setActive([]);
    ed.setTransforming(false);
    ed.guides = [];
    ed.renderer.invalidateInteractive();
  }

  private startMove(x: number, y: number, duplicate: boolean): void {
    const ed = this.ed;
    ed.beginGesture();
    if (duplicate) {
      const clones = ed.cloneObjects(ed.selectedObjects(), 0, 0);
      ed.apply(clones.map((obj) => ({ t: 'create', obj })));
      ed.setSelection(clones.map((c) => c.id));
    }
    const originals = this.beginTransform(true);
    const bounds = union([...originals.values()].map((o) => ed.boundsOf(o))) ?? { x, y, w: 0, h: 0 };
    this.state = { kind: 'moving', x, y, originals, bounds, cards: this.columnDrag(originals, x, y) };
  }

  /** Dragging only cards, with columns on the board: they stack into whichever column they're dropped on. */
  private columnDrag(originals: Originals, x: number, y: number): ColumnDrag | undefined {
    const ed = this.ed;
    const objs = [...originals.values()];
    const cols = ed.columnList();
    if (!cols.length || !objs.every((o) => o.type === 'card')) return undefined;
    const cards = byPosition(objs as Card[]);
    const lead = cards.find((c) => x >= c.x && x <= c.x + c.w && y >= c.y && y <= c.y + c.h) ?? cards[0]!;
    const sources = new Set(cols.filter((c) => cards.some((card) => c.children.includes(card.id))).map((c) => c.id));
    return { ids: cards.map((c) => c.id), lead: lead.id, sources, target: null, index: -1 };
  }

  /** Where the dragged cards would land now: the column under the lead card's centre, and the place in it. */
  private dropSpot(d: ColumnDrag): { target: ReturnType<typeof columnAt>; index: number; cards: Card[] } {
    const ed = this.ed;
    const cards = d.ids.map((id) => ed.store.get(id) as Card);
    const lead = ed.store.get(d.lead) as Card;
    const target = columnAt(ed.store, ed.columnList(), lead.x + lead.w / 2, lead.y + lead.h / 2);
    const top = cards[0]!;
    const index = target ? dropIndex(ed.store, target, ed.heightAt, new Set(d.ids), top.y + top.h / 2) : 0;
    return { target, index, cards };
  }

  /** While dragging: the column under the cards opens a gap where they'd land; the ones they left close up. */
  private previewColumns(d: ColumnDrag): void {
    const ed = this.ed;
    const { target, index, cards } = this.dropSpot(d);
    if ((target?.id ?? null) === d.target && index === d.index) return;
    const relayout = new Set([...d.sources, ...(d.target ? [d.target] : []), ...(target ? [target.id] : [])]);
    const gap = target ? { column: target.id, index, size: roomFor(cards, target, ed.heightAt) } : undefined;
    const ops = layoutOps(ed.store, ed.columnList(), ed.heightAt, { columns: relayout, exclude: new Set(d.ids), gap });
    if (ops.length) ed.apply(ops);
    d.target = target?.id ?? null;
    d.index = index;
  }

  /** Dropping: the cards take their place in the column (or leave their columns for the canvas). */
  private dropCards(d: ColumnDrag): void {
    const ed = this.ed;
    const { target, index } = this.dropSpot(d);
    const dragged = new Set(d.ids);
    const orders = new Map<string, string[]>();
    for (const id of d.sources) {
      const c = ed.store.get(id);
      if (isColumn(c)) orders.set(id, members(ed.store, c).map((m) => m.id).filter((m) => !dragged.has(m)));
    }
    if (target) {
      const others = orders.get(target.id) ?? members(ed.store, target).map((m) => m.id).filter((m) => !dragged.has(m));
      orders.set(target.id, [...others.slice(0, index), ...d.ids, ...others.slice(index)]);
    }
    const ops = layoutOps(ed.store, ed.columnList(), ed.heightAt, { columns: orders.keys(), order: orders });
    if (ops.length) ed.apply(ops);
  }

  /** The resize handle under a point. A column's height follows its cards, so it only resizes sideways. */
  private handleAt(frame: Box, x: number, y: number): Handle | null {
    const ed = this.ed;
    if (this.singleConnector()) return null;
    const h = handleAt(frame, ed.camera.zoom, x, y, this.canRotate());
    if (!h || !ed.selectedObjects().some(isColumn)) return h;
    return h === 'e' || h === 'ne' || h === 'se' ? 'e' : h === 'w' || h === 'nw' || h === 'sw' ? 'w' : null;
  }

  private updateMove(e: WorldPointerEvent): void {
    const s = this.state;
    if (s.kind !== 'moving') return;
    const ed = this.ed;
    let dx = e.x - s.x, dy = e.y - s.y;
    ed.guides = [];
    if (!e.modKey) {
      const moved = { ...s.bounds, x: s.bounds.x + dx, y: s.bounds.y + dy };
      const snap = snapMove(moved, ed.snapCandidates(s.originals), SNAP_PX / ed.camera.zoom);
      dx += snap.dx;
      dy += snap.dy;
      ed.guides = snap.guides;
    }
    const ops: Op[] = [];
    for (const o of s.originals.values()) ops.push({ t: 'update', id: o.id, props: ed.moveProps(o, dx, dy) });
    ed.apply(ops);
    if (s.cards) this.previewColumns(s.cards);
    ed.renderer.invalidateInteractive();
  }

  private updateResize(s: Extract<State, { kind: 'resizing' }>, e: WorldPointerEvent): void {
    const ed = this.ed;
    const { frame, handle } = s;
    const keepAspect = s.keepAspect || e.shiftKey;
    let px = e.x, py = e.y;
    let snapX: SnapMatch | null = null, snapY: SnapMatch | null = null;
    if (!e.modKey && !keepAspect && frame.rotation === 0) {
      const candidates = ed.snapCandidates(s.originals);
      const threshold = SNAP_PX / ed.camera.zoom;
      if (/[ew]/.test(handle) && (snapX = snapValue(px, candidates, 'x', threshold))) px = snapX.target;
      if (/[ns]/.test(handle) && (snapY = snapValue(py, candidates, 'y', threshold))) py = snapY.target;
    }

    const [lx, ly] = toLocal(frame, px, py);
    const local = resizeLocal(frame, handle, lx, ly, keepAspect);
    const [cx, cy] = fromLocal(frame, local.x + local.w / 2, local.y + local.h / 2);
    const next: Box = { x: cx - local.w / 2, y: cy - local.h / 2, w: local.w, h: local.h, rotation: frame.rotation };

    ed.guides = [];
    if (snapX) ed.guides.push(guideFor(snapX, 'x', next));
    if (snapY) ed.guides.push(guideFor(snapY, 'y', next));
    ed.apply(scaleOps(s.originals, frame, next));
    // A column's cards follow its new width as it's dragged (spec §13.6).
    const cols = [...s.originals.values()].filter(isColumn).map((c) => c.id);
    if (cols.length) ed.restackColumns(cols);
    ed.renderer.invalidateInteractive();
  }

  private updateRotate(s: Extract<State, { kind: 'rotating' }>, e: WorldPointerEvent): void {
    let delta = Math.atan2(e.y - s.cy, e.x - s.cx) - s.startAngle;
    if (e.shiftKey) delta = Math.round((s.baseRotation + delta) / ROTATE_STEP) * ROTATE_STEP - s.baseRotation;
    const ops: Op[] = [];
    for (const o of s.originals.values()) {
      if (o.type === 'connector') {
        const turn = (ep: Endpoint): Endpoint => ('point' in ep ? { point: rotatePoint(ep.point[0], ep.point[1], s.cx, s.cy, delta) } : ep);
        ops.push({ t: 'update', id: o.id, props: { from: turn(o.from), to: turn(o.to) } });
        continue;
      }
      const [ncx, ncy] = rotatePoint(o.x + o.w / 2, o.y + o.h / 2, s.cx, s.cy, delta);
      ops.push({
        t: 'update', id: o.id,
        props: { x: ncx - o.w / 2, y: ncy - o.h / 2, rotation: normalizeAngle(o.rotation + delta) },
      });
    }
    this.ed.apply(ops);
    this.ed.renderer.invalidateInteractive();
  }

  private updateMarquee(s: Extract<State, { kind: 'marquee' }>, e: WorldPointerEvent): void {
    const ed = this.ed;
    const r = rectFromPoints(s.x, s.y, e.x, e.y);
    ed.marquee = r;
    const next = new Set(s.base);
    for (const id of ed.index.query(r)) next.add(id);
    ed.setSelection(next);
    ed.renderer.invalidateInteractive();
  }

  /** The single selected connector, if that's the whole selection. */
  singleConnector(): Connector | null {
    const ed = this.ed;
    if (ed.selection.size !== 1) return null;
    const o = ed.store.get(ed.selection.values().next().value!);
    return o?.type === 'connector' ? o : null;
  }

  /** Frames and connectors don't rotate. */
  private canRotate(): boolean {
    return this.ed.selectedObjects().every((o) => o.type !== 'frame' && o.type !== 'connector');
  }

  private endpointHandleAt(wx: number, wy: number): { id: string; end: 'from' | 'to' } | null {
    const c = this.ed.readOnly ? null : this.singleConnector();
    if (!c) return null;
    const r = ENDPOINT_HIT / this.ed.camera.zoom;
    for (const end of ['to', 'from'] as const) {
      const [x, y] = endpointPosition(c, end, (id) => this.ed.store.get(id));
      if (Math.abs(wx - x) <= r && Math.abs(wy - y) <= r) return { id: c.id, end };
    }
    return null;
  }

  /** Re-attaches (or frees) one end of a connector as it's dragged. */
  private updateEndpoint(s: Extract<State, { kind: 'endpoint' }>, e: WorldPointerEvent): void {
    const ed = this.ed;
    const c = ed.store.get(s.id);
    if (c?.type !== 'connector') return;
    const other = c[s.end === 'from' ? 'to' : 'from'];
    const exclude = new Set('objectId' in other ? [other.objectId] : []);
    const { ep, target } = endpointAt(ed, e.x, e.y, exclude);
    ed.attachTarget = target?.id ?? null;
    ed.apply([{ t: 'update', id: s.id, props: { [s.end]: ep } }]);
    ed.renderer.invalidateInteractive();
  }

  private hover(e: WorldPointerEvent): void {
    const ed = this.ed;
    const frame = ed.readOnly ? null : selectionFrame(ed.store, ed.selection, (o) => ed.boundsOf(o));
    const handle = frame && this.handleAt(frame, e.x, e.y);
    const cursor = this.endpointHandleAt(e.x, e.y) ? 'move'
      : frame && handle ? handleCursor(handle, frame.rotation)
      : ed.columnFooterAt(e.x, e.y) || ed.audioControlAt(e.x, e.y) ? 'pointer'
      : e.modKey && ed.linkAt(e.x, e.y) ? 'pointer'
      : !ed.readOnly && ed.tickBoxAt(e.x, e.y) ? 'pointer' : 'default';
    if (cursor !== this.cursor) {
      this.cursor = cursor;
      ed.refreshCursor();
    }
    const hovered = handle ? null : ed.hitTest(e.x, e.y)?.id ?? null;
    if (hovered !== ed.hovered) {
      ed.hovered = hovered;
      ed.renderer.invalidateInteractive();
    }
  }
}

function insideFrame(frame: Box, wx: number, wy: number): boolean {
  const [lx, ly] = toLocal(frame, wx, wy);
  return lx >= 0 && ly >= 0 && lx <= frame.w && ly <= frame.h;
}

/** Maps each original from the `from` frame into the `to` frame. */
function scaleOps(originals: Originals, from: Box, to: Box): Op[] {
  if (originals.size === 1) {
    const o = originals.values().next().value!;
    return [{ t: 'update', id: o.id, props: { x: to.x, y: to.y, w: to.w, h: to.h } }];
  }
  // Multi-selection frames are axis-aligned: scale each object's centre and size.
  const sx = to.w / from.w, sy = to.h / from.h;
  const ops: Op[] = [];
  const map = (ep: Endpoint): Endpoint =>
    'point' in ep ? { point: [to.x + (ep.point[0] - from.x) * sx, to.y + (ep.point[1] - from.y) * sy] } : ep;
  for (const o of originals.values()) {
    if (o.type === 'connector') {
      ops.push({ t: 'update', id: o.id, props: { from: map(o.from), to: map(o.to) } });
      continue;
    }
    const cx = to.x + (o.x + o.w / 2 - from.x) * sx;
    const cy = to.y + (o.y + o.h / 2 - from.y) * sy;
    const w = o.w * sx, h = o.h * sy;
    ops.push({ t: 'update', id: o.id, props: { x: cx - w / 2, y: cy - h / 2, w, h } });
  }
  return ops;
}
